import type { NoteName } from "./guitarEngine";
import type { PianoFocusStep, PianoIntervalDirection } from "./pianoPedagogy";
import { pianoIntervalLabel } from "./pianoPedagogy";
import {
  MIDDLE_C,
  PIANO_LEVEL1_STEPS,
  pianoStepDescription,
  rootMidiAtOctave,
  buildPianoLevel1PlaybackMidis,
} from "./pianoPedagogy";

export interface PianoLearnIntroContent {
  title: string;
  missionText: string;
  steps: string[];
  footerTip: string;
  buttonText?: string;
}

function rootLabel(root: NoteName, octave = 4): string {
  return `${root}${octave}`;
}

/** Textos del modal «Aprender» — pentagrama + piano, sin reproducción automática. */
export function getPianoLearnIntro(
  step: PianoFocusStep,
  root: NoteName,
  scaleLabel: string
): PianoLearnIntroContent {
  const c4 = rootLabel("C", 4);

  switch (step) {
    case 1:
      return {
        title: `Paso 1 · La tónica de ${root}`,
        missionText: `La tónica es la nota que da nombre a la escala ${root} ${scaleLabel}. Te vamos a pedir que la encuentres en dos octavas distintas: ${root}4 y ${root}5. El pentagrama de arriba muestra exactamente esas dos notas que debés ubicar en el teclado.`,
        steps: [
          `Mirá el pentagrama: hay dos ${root} a la vista, una grave y otra una octava más aguda`,
          `Ubicá el Do central (${c4}) en el teclado para orientarte`,
          `Tocá la tecla ${root}4 (cerca del Do central, mano izquierda)`,
          `Tocá la tecla ${root}5 (una octava más arriba, mano derecha)`,
        ],
        footerTip:
          "Sin micrófono ni reproducción: tomate tu tiempo. El feedback verde confirma cada tecla correcta.",
        buttonText: "Empezar exploración",
      };
    case 2:
      return {
        title: `Paso 2 · Las 2das de ${root}`,
        missionText: `Cada nota de la escala ${root} ${scaleLabel} puede encontrarse subiendo (adelante) o bajando (atrás) desde la tónica. En la escala diatónica de ${root}: la 2ª hacia arriba es una segunda mayor (2M, paso entero); la 2ª hacia abajo es una segunda menor (2m, un semitono). Primero buscarás la ascendente, luego la descendente.`,
        steps: [
          "Adelante en el teclado = ascendente · Atrás = descendente",
          `2ª ascendente (2M): ${pianoIntervalLabel(2, "asc", root)}`,
          `2ª descendente (2m): ${pianoIntervalLabel(2, "desc", root)}`,
          "El pentagrama mostrará solo la nota que debés encontrar en cada ronda",
        ],
        footerTip:
          "En Do mayor: Re es 2M arriba; Si es 2m abajo. No confundas «bajar en la escala» con «bajar un traste».",
        buttonText: "Buscar la 2ª ascendente",
      };
    case 3:
      return {
        title: `Paso 3 · Las 5tas de ${root}`,
        missionText: `La 5ª también tiene versión ascendente y descendente respecto a la tónica. En ${root} ${scaleLabel} ambas son quintas justas (5J): un salto estable hacia adelante o hacia atrás en el teclado. Primero la ascendente, después la descendente.`,
        steps: [
          "Adelante = 5ª ascendente · Atrás = 5ª descendente",
          `5ª ascendente: ${pianoIntervalLabel(3, "asc", root)}`,
          `5ª descendente: ${pianoIntervalLabel(3, "desc", root)}`,
          "Usá la tónica resaltada como punto de referencia en cada ronda",
        ],
        footerTip: "La quinta suena «abierta» y de reposo; es el segundo ancla de la escala después de la tónica.",
        buttonText: "Buscar la 5ª ascendente",
      };
    case 4:
      return {
        title: `Paso 4 · Camino I–V (5→4→3→2→1)`,
        missionText: `Ahora vas a recorrer las cinco notas seguidas de la escala ${root} ${scaleLabel}, desde la tónica hasta la 5ª. El teclado muestra los dedos sugeridos sobre cada tecla: empezás con el meñique (5) y terminás con el pulgar (1) en la quinta.`,
        steps: [
          "Leé la secuencia en el pentagrama de izquierda a derecha (5 notas seguidas)",
          "Posicioná la mano izquierda con el meñique (dedo 5) sobre la tónica",
          "Tocá las cinco teclas en orden ascendente sin levantar la mano",
          "Repetí el camino dos o tres veces para fijar la posición",
        ],
        footerTip: "Esta posición fija de 5 dedos es la base de la técnica clásica de teclado.",
        buttonText: "Recorrer el camino",
      };
    case 5:
      return {
        title: `Paso 5 · «Los pollitos dicen» en ${root}`,
        missionText: `Tu primera canción en ${root} ${scaleLabel}. El pentagrama muestra la melodía completa coloreada por grados; el teclado te indica cada tecla en orden. No buscamos tempo todavía: solo que reconozcas la melodía nota por nota.`,
        steps: [
          "Mirá el pentagrama: la melodía es «Do re mi fa sol sol — la la sol sol…»",
          "Empezá tocando la primera nota destacada en el teclado",
          "Avanzá tecla por tecla siguiendo el orden resaltado",
          "Una vez que reconozcas la canción, podés usar «Practicar» con micrófono",
        ],
        footerTip: "El objetivo es leer y reconocer, no la velocidad. Felicitaciones por llegar al final del nivel.",
        buttonText: "Ver la melodía en el teclado",
      };
    default:
      return getPianoLearnIntro(1, root, scaleLabel);
  }
}

/** MIDI que el alumno debe reconocer en el paso actual (para validación por clic). */
export function getPianoLearnTargetMidis(
  step: PianoFocusStep,
  root: NoteName,
  scaleType: string,
  intervalDir?: PianoIntervalDirection
): number[] {
  return buildPianoLevel1PlaybackMidis(root, scaleType, step, intervalDir);
}

/** Contenido del modal de felicitación al terminar un paso de exploración piano. */
export function getPianoStepCompleteContent(
  step: PianoFocusStep,
  root: NoteName,
  scaleLabel: string
): PianoLearnIntroContent {
  const meta = PIANO_LEVEL1_STEPS.find((s) => s.level === step);
  const stepLabel = meta?.label ?? `Paso ${step}`;
  const nextMeta = PIANO_LEVEL1_STEPS.find((s) => s.level === step + 1);
  const nextHint = nextMeta
    ? `Siguiente: ${nextMeta.label} — ${pianoStepDescription((step + 1) as PianoFocusStep)}`
    : "Usá «Practicar» cuando quieras validar con el micrófono.";

  const base = {
    message: pianoStepDescription(step),
    buttonText: step < 5 ? "Siguiente paso" : "Seguir practicando",
  };

  switch (step) {
    case 1:
      return {
        title: `¡${stepLabel} de ${root} — listo!`,
        ...base,
        steps: [
          "Relacionaste la tónica en el pentagrama (clave de sol) con el teclado",
          `Ubicaste ${root} partiendo del Do central (${rootLabel("C", 4)})`,
          "Comprobaste la nota con clic y feedback verde/rojo",
        ],
        footerTip: nextHint,
      };
    case 2:
      return {
        title: `¡${stepLabel} — listo!`,
        ...base,
        steps: [
          "Identificaste la tónica y las 2das en el pentagrama",
          "Tocaste o hiciste clic en las tres teclas resaltadas",
          "Distinguiste subir (2ª ascendente) y bajar (2ª descendente)",
        ],
        footerTip: nextHint,
      };
    case 3:
      return {
        title: `¡${stepLabel} — listo!`,
        ...base,
        steps: [
          "Reconociste la tónica y las quintas en la partitura",
          "Encontraste las tres teclas en el piano",
          "Relacionaste el sonido «abierto» de la 5ª con la tónica",
        ],
        footerTip: nextHint,
      };
    case 4:
      return {
        title: `¡${stepLabel} — listo!`,
        ...base,
        steps: [
          "Recorriste las cinco notas del camino tónica → quinta",
          "Seguiste la secuencia del pentagrama en el teclado",
          "Usaste la numeración de dedos sugerida (5 a 1)",
        ],
        footerTip: nextHint,
      };
    case 5:
      return {
        title: `¡${stepLabel} — listo!`,
        message: `Reconociste «Los pollitos» en ${root} ${scaleLabel} en pentagrama y teclado.`,
        steps: [
          "Seguiste la melodía nota a nota en el pentagrama",
          "Ubicaste cada tecla coloreada en el piano",
          "Conectaste lectura y ejecución sin apuro de tempo",
        ],
        footerTip: nextHint,
        buttonText: "Seguir practicando",
      };
    default:
      return getPianoStepCompleteContent(1, root, scaleLabel);
  }
}

/** Modal breve al pasar de ascendente → descendente en pasos 2 y 3. */
export function getPianoIntervalTransitionIntro(
  step: 2 | 3,
  direction: PianoIntervalDirection,
  root: NoteName,
  scaleLabel: string
): PianoLearnIntroContent {
  const label = pianoIntervalLabel(step, direction, root);
  if (step === 2 && direction === "desc") {
    return {
      title: "Ahora la 2ª descendente",
      missionText: `¡Bien! Encontraste la 2ª ascendente (2M). Ahora buscá la 2ª descendente (2m): ${label}. Es la nota un semitono atrás desde ${root} en la escala diatónica.`,
      steps: [
        "Mirá el pentagrama: ahora solo se resalta la nota «atrás»",
        `Localizá ${label} en el teclado`,
        "Hacé clic en la tecla correcta para confirmar",
      ],
      footerTip: "2M arriba, 2m abajo — así se construye el mapa de la escala.",
      buttonText: "Buscar la 2ª descendente",
    };
  }
  if (step === 3 && direction === "desc") {
    return {
      title: "Ahora la 5ª descendente",
      missionText: `¡Bien! Encontraste la 5ª ascendente. Ahora buscá la 5ª descendente: ${label}, cinco grados hacia atrás desde ${root} ${scaleLabel}.`,
      steps: [
        "La tónica sigue visible como referencia",
        `Encontrá ${label} en el teclado`,
        "Confirmá con un clic",
      ],
      footerTip: "Ascendente y descendente completan tu orientación en el registro.",
      buttonText: "Buscar la 5ª descendente",
    };
  }
  return getPianoLearnIntro(step, root, scaleLabel);
}

export { MIDDLE_C, rootMidiAtOctave };
